<template>
  <div>
    <h2 class="mb-4">จัดการหนังสือ</h2>
    <CRow>
      <CCol md="3">
        <CInput
          v-model="word"
          placeholder="ค้นหา"
          @keyup.enter="handleSearch"
        />
      </CCol>
      <CCol md="3">
        <CSelect :value.sync="filterSelected" :options="filter" />
      </CCol>
      <CCol md="6">
        <div class="flex space-between mb-3">
          <CButton color="primary" @click="handleSearch">
            <i class="fas fa-search"></i> ค้นหา
          </CButton>
          <CButton color="info" @click="handleDownload" :disabled="downloading">
            <i
              :class="
                downloading ? 'fas fa-spinner fa-spin' : 'far fa-file-excel'
              "
            ></i>
            {{ downloading ? 'Downloading...' : 'Excel download' }}
          </CButton>
        </div>
      </CCol>
    </CRow>
    <CRow>
      <CCol col="12">
        <vue-good-table
          mode="remote"
          :isLoading.sync="loading"
          :columns="columns"
          :rows="rows"
          :sort-options="{
            enabled: true,
            initialSortBy: defaultSearch.sort
          }"
          @on-sort-change="onSortChange"
        >
          <template slot="table-row" slot-scope="props">
            <span v-if="props.column.field == 'createdAt'">
              {{ props.row.createdAt | datetimeFormat }}
            </span>
            <span v-else-if="props.column.field == 'action'">
              <CButtonGroup class="m-1">
                <CButton
                  size="sm"
                  color="success"
                  :variant="props.row.exportStatus === 1 ? 'outline' : ''"
                  :disabled="props.row.exportStatus === 1"
                  @click="getFfmpegs(props.row.id)"
                >
                  {{
                    props.row.exportStatus === 1 ? 'Exporting' : 'Export'
                  }}
                  <span class="sr-only">{{props.row.exportStatus === 1 ? 'กำลัง Export หนังสือ' : 'สั่ง Export หนังสือ'}}</span>
                </CButton>
                <CButton
                  size="sm"
                  color="info"
                  :variant="props.row.exportStatus !== 2 ? 'outline' : ''"
                  :disabled="props.row.exportStatus !== 2"
                >
                  <a :href="props.row.downloadUrl">Download</a>
                  <span class="sr-only">ดาวน์โหลดหนังสืออีพับ</span>
                </CButton>
              </CButtonGroup>
              <CButton
                color="info"
                variant="outline"
                size="sm"
                class="m-1"
                @click="$refs.notiModal.show(props.row.readerId)"
              >
                <i class="fa fa-bell"></i>
                <span class="sr-only">ส่งข้อความถึงอาสาที่อ่านหนังสือเล่มนี้</span>
              </CButton>
              <CButton
                color="info"
                size="sm"
                class="m-1"
                @click="$refs.infoModal.show(props.row.id)"
              >
                <i class="fa fa-info"></i>
                <span class="sr-only">ดูข้อมูลหนังสือ</span>
              </CButton>
              <CButton
                color="warning"
                size="sm"
                class="m-1"
                @click="$refs.editModal.show(props.row)"
              >
                <i class="fa fa-pen"></i>
                <span class="sr-only">แก้ไขข้อมูลหนังสือ</span>
              </CButton>
              <CButton
                color="danger"
                variant="outline"
                size="sm"
                class="m-1"
                @click="$refs.deleteModal.show(props.row.id, props.row.name)"
              >
                <i class="fa fa-trash"></i>
                <span class="sr-only">ลบหนังสือ</span>
              </CButton>
            </span>
            <span v-else class="h6">
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
        </vue-good-table>
      </CCol>
    </CRow>
    <CRow>
      <CCol col="12">
        <vue-ads-pagination
          :total-items="total"
          :max-visible-pages="5"
          :items-per-page="10"
          :page="search.page"
          :loading="loading"
        >
          <template slot-scope="props">
            <div class="vue-ads-pr-2 vue-ads-leading-loose">
              รายการที่ {{ props.start | decimalFormat }} ถึง
              {{ props.end | decimalFormat }} จาก
              {{ props.total | decimalFormat }} รายการ
            </div>
          </template>
          <template slot="buttons" slot-scope="props">
            <vue-ads-page-button
              v-for="(button, key) in props.buttons"
              :key="key"
              v-bind="button"
              @page-change="pageChange(button.page)"
            />
          </template>
        </vue-ads-pagination>
      </CCol>
    </CRow>

    <UserNotiModal ref="notiModal" />
    <BookInfoModal ref="infoModal" />
    <BookEditModal ref="editModal" :onComplete="getList" />
    <DeleteModal ref="deleteModal" :onConfirm="deleteBook" />
  </div>
</template>

<script>
import moment from 'moment'

import UserNotiModal from '../modal/UserNotiModal'
import BookInfoModal from '../modal/BookInfoModal'
import BookEditModal from '../modal/BookEditModal'
import DeleteModal from '../modal/DeleteModal'

import apiBook from '@/apis/book'

export default {
  name: 'Book',
  components: {
    UserNotiModal,
    BookInfoModal,
    BookEditModal,
    DeleteModal
  },
  data() {
    return {
      loading: false,
      downloading: false,
      word: '',
      search: {},
      defaultSearch: {
        page: 0,
        limit: 10,
        sort: {
          field: 'createdAt',
          type: 'desc'
        }
      },
      total: 0,
      filterSelected: 'name',
      filter: [
        { value: 'name', label: 'ชื่อหนังสือ' },
        { value: 'authorName', label: 'ชื่อผู้แต่ง' },
        { value: 'translatorName', label: 'ชื่อผู้แปล' }
      ],
      columns: [
        {
          label: 'ลำดับ',
          field: 'no',
          width: '50px',
          thClass: 'text-center',
          tdClass: 'text-center',
          sortable: false
        },
        {
          label: 'ชื่อหนังสือ',
          field: 'name',
          width: '180px'
        },
        {
          label: 'ผู้แต่ง',
          field: 'authorName',
          width: '150px'
        },
        {
          label: 'ผู้แปล',
          field: 'translatorName',
          width: '150px'
        },
        {
          label: 'ผู้อ่าน',
          field: 'readerName',
          width: '150px',
          sortable: false
        },
        {
          label: 'วันเวลาที่สร้าง',
          field: 'createdAt',
          width: '160px'
        },
        {
          label: 'ความสมบูรณ์ (%)',
          field: 'percent',
          tdClass: 'text-right',
          width: '140px'
        },
        {
          label: 'Action',
          field: 'action',
          sortable: false,
          tdClass: 'text-center',
          width: '300px'
        }
      ],
      rows: []
    }
  },
  created() {
    this.search = Object.assign({}, this.defaultSearch)
  },
  methods: {
    handleSearch() {
      this.search = Object.assign({}, this.defaultSearch)
      if (this.word) this.search[this.filterSelected] = this.word

      this.getList()
    },
    handleDownload() {
      this.downloading = true
      apiBook
        .download()
        .then((data) => {
          const blob = new Blob([data], { type: 'text/csv' })
          const link = document.createElement('a')
          link.href = URL.createObjectURL(blob)

          link.target = '_blank'
          link.download = `${moment().format('YYYYMMDDHHmm')}_books.csv`
          link.click()
          URL.revokeObjectURL(link.href)

          this.downloading = false
        })
        .catch(() => {
          this.downloading = false
        })
    },
    pageChange(page) {
      this.search.page = page
      this.getList()
    },
    onSortChange(params) {
      this.search.sort = params[0]
      this.getList()
    },
    getList() {
      this.loading = true
      apiBook
        .getList(this.search)
        .then((result) => {
          this.total = result.data.totalDocs
          this.rows = []

          for (let i = 0; i < result.data.docs.length; i++) {
            const item = result.data.docs[i]
            this.rows.push({
              no: this.search.page * this.search.limit + i + 1,
              id: item.id,
              name: item.name,
              authorName: item.authorName,
              translatorName: item.translatorName,
              readerName: item.readerName,
              readerId: item.readerId,
              percent: item.percent,
              createdAt: item.createdAt,
              exportStatus: item.exportStatus,
              downloadUrl: item.downloadUrl
            })
          }
        })
        .catch(() => {
          this.rows = []
          this.loading = false
        })
    },
    getFfmpegs(id) {
      apiBook.ffmpegs(id).then(() => {
        this.getList()
      })
    },
    downloadFfmpegs(url) {
      window.location.href = url
    },
    deleteBook(id) {
      this.loading = true
      apiBook
        .remove(id)
        .then(() => {
          this.getList()
        })
        .catch(() => {
          this.loading = false
        })
    }
  }
}
</script>
